import React, { useEffect, useRef, useState } from "react";
import { Stage, Layer, Rect } from "react-konva";
import styles from "../create-feedback/CreateFeedback.module.scss";

/**
 * AnnotationStage
 * - Finalized rectangles are sent to parent as normalized coords { x1,y1,x2,y2 } in 0..1
 * - Renders annotations from normalized coords (or converts pixel coords to normalized if detected)
 * - Preview supports drawing in any direction and is clamped to the visible video area
 *
 * Props (unchanged):
 *  videoRef, playerRef, annotations, newAnnotation, setNewAnnotation,
 *  drawing, setDrawing, isPaused, videoDimensions, onContentRectChange
 */
const AnnotationStage = ({
  videoRef,
  playerRef,
  annotations = [],
  newAnnotation,
  setNewAnnotation,
  drawing,
  setDrawing,
  isPaused,
  videoDimensions,
  onContentRectChange,
  isPlayerSide = false,
  absoluteMode = false,
}) => {
  const stageRef = useRef();
  const rafRef = useRef(null);
  const resizeObsRef = useRef(null);
  const wrapperMutObserverRef = useRef(null);

  // visible content area of the video (viewport coords)
  const [contentRect, setContentRect] = useState({
    width: 0,
    height: 0,
    offsetX: 0,
    offsetY: 0,
    top: 0,
    left: 0,
  });

  // debounce measurement via rAF
  const scheduleMeasure = () => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      measure();
    });
  };

  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

  // measure visible video content (handles letterbox/pillarbox)
  const measure = () => {
    if (!videoRef || !videoRef.current) return;

    const videoEl = videoRef.current;
    const videoRect = videoEl.getBoundingClientRect();

    const intrinsicW = videoEl.videoWidth || videoEl.naturalWidth || 0;
    const intrinsicH = videoEl.videoHeight || videoEl.naturalHeight || 0;

    const displayW = Math.max(0, videoRect.width);
    const displayH = Math.max(0, videoRect.height);

    let contentW = displayW;
    let contentH = displayH;
    let offsetX = 0;
    let offsetY = 0;

    if (intrinsicW > 0 && intrinsicH > 0 && displayW > 0 && displayH > 0) {
      const vAspect = intrinsicW / intrinsicH;
      const dAspect = displayW / displayH;

      if (dAspect > vAspect) {
        // pillarbox
        contentH = displayH;
        contentW = Math.round(contentH * vAspect);
        offsetX = Math.round((displayW - contentW) / 2);
        offsetY = 0;
      } else {
        // letterbox
        contentW = displayW;
        contentH = Math.round(contentW / vAspect);
        offsetX = 0;
        offsetY = Math.round((displayH - contentH) / 2);
      }
    }

    const top = Math.round(videoRect.top + offsetY);
    const left = Math.round(videoRect.left + offsetX);

    const nextRect = {
      width: Math.round(contentW),
      height: Math.round(contentH),
      offsetX,
      offsetY,
      top,
      left,
    };

    setContentRect((prev) => {
      const changed =
        prev.width !== nextRect.width ||
        prev.height !== nextRect.height ||
        prev.top !== nextRect.top ||
        prev.left !== nextRect.left;
      if (changed) {
        return nextRect;
      }
      return prev;
    });
  };

  // Notify parent when contentRect changes (separate from setState to avoid warnings)
  useEffect(() => {
    if (typeof onContentRectChange === "function" && contentRect.width > 0 && contentRect.height > 0) {
      try {
        onContentRectChange(contentRect);
      } catch (e) { }
    }
  }, [contentRect, onContentRectChange]);


  // observe layout/DOM/resize events
  useEffect(() => {
    if (!videoRef || !videoRef.current) return;
    const videoEl = videoRef.current;

    scheduleMeasure();

    const onLoadedMeta = () => scheduleMeasure();
    videoEl.addEventListener("loadedmetadata", onLoadedMeta);

    if (typeof ResizeObserver !== "undefined") {
      resizeObsRef.current = new ResizeObserver(() => scheduleMeasure());
      resizeObsRef.current.observe(videoEl);
      const wrapperEl = (videoEl.closest && videoEl.closest(".video-js")) || videoEl.parentElement || null;
      if (wrapperEl) resizeObsRef.current.observe(wrapperEl);
    } else {
      window.addEventListener("resize", scheduleMeasure);
    }

    const wrapperEl = (videoEl.closest && videoEl.closest(".video-js")) || videoEl.parentElement || null;
    if (wrapperEl && typeof MutationObserver !== "undefined") {
      wrapperMutObserverRef.current = new MutationObserver(() => scheduleMeasure());
      wrapperMutObserverRef.current.observe(wrapperEl, { childList: true, subtree: true });
    }

    const onScroll = () => scheduleMeasure();
    const onOrientation = () => scheduleMeasure();
    const onFullscreen = () => scheduleMeasure();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("orientationchange", onOrientation);
    window.addEventListener("resize", scheduleMeasure);
    document.addEventListener("fullscreenchange", onFullscreen);
    document.addEventListener("webkitfullscreenchange", onFullscreen);
    document.addEventListener("msfullscreenchange", onFullscreen);

    const player = playerRef?.current;
    if (player && typeof player.on === "function") {
      try {
        player.on("resize", scheduleMeasure);
        player.on("fullscreenchange", scheduleMeasure);
      } catch (e) { }
    }

    return () => {
      videoEl.removeEventListener("loadedmetadata", onLoadedMeta);
      if (resizeObsRef.current && typeof resizeObsRef.current.disconnect === "function") {
        resizeObsRef.current.disconnect();
      } else {
        window.removeEventListener("resize", scheduleMeasure);
      }
      if (wrapperMutObserverRef.current && typeof wrapperMutObserverRef.current.disconnect === "function") {
        wrapperMutObserverRef.current.disconnect();
      }
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("orientationchange", onOrientation);
      document.removeEventListener("fullscreenchange", onFullscreen);
      document.removeEventListener("webkitfullscreenchange", onFullscreen);
      document.removeEventListener("msfullscreenchange", onFullscreen);

      if (player && typeof player.off === "function") {
        try {
          player.off("resize", scheduleMeasure);
          player.off("fullscreenchange", scheduleMeasure);
        } catch (e) { }
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoRef?.current, playerRef?.current]);

  // pointer (stage) position clamped to content area
  const getPointer = (e) => {
    const stage = e.target.getStage();
    const p = (stage && stage.getPointerPosition && stage.getPointerPosition()) || { x: 0, y: 0 };
    if (contentRect && contentRect.width && contentRect.height) {
      return {
        x: clamp(p.x, 0, contentRect.width),
        y: clamp(p.y, 0, contentRect.height),
      };
    }
    return p;
  };

  // Start drawing
  const handleStartDrawing = (e) => {
    if (!contentRect || contentRect.width === 0 || contentRect.height === 0) return;
    const { x, y } = getPointer(e);
    // store start and current in stage coords for preview
    setNewAnnotation({ startX: x, startY: y, x, y, width: 0, height: 0 });
    setDrawing(true);
  };

  // During draw - update preview in stage coords (parent receives preview)
  const handleDrawing = (e) => {
    if (!drawing) return;
    const { x, y } = getPointer(e);
    setNewAnnotation((prev) => {
      if (!prev) return { startX: x, startY: y, x, y, width: 0, height: 0 };
      const width = clamp(x - (prev.startX ?? prev.x ?? 0), -contentRect.width, contentRect.width);
      const height = clamp(y - (prev.startY ?? prev.y ?? 0), -contentRect.height, contentRect.height);
      return {
        ...prev,
        x: prev.startX ?? prev.x ?? prev.startX,
        y: prev.startY ?? prev.y ?? prev.startY,
        width,
        height,
      };
    });
  };

  // finish drawing: normalize and send normalized coords to parent
  const handleEndDrawing = () => {
    if (!newAnnotation) {
      setDrawing(false);
      return;
    }

    const startX = newAnnotation.startX ?? newAnnotation.x ?? 0;
    const startY = newAnnotation.startY ?? newAnnotation.y ?? 0;
    let w = newAnnotation.width ?? 0;
    let h = newAnnotation.height ?? 0;

    // convert to positive x,y,width,height in stage coordinates
    let finalX = w >= 0 ? startX : startX + w;
    let finalY = h >= 0 ? startY : startY + h;
    let finalW = Math.abs(w);
    let finalH = Math.abs(h);

    // clamp to contentRect
    finalX = clamp(finalX, 0, contentRect.width);
    finalY = clamp(finalY, 0, contentRect.height);
    finalW = Math.max(0, Math.min(finalW, contentRect.width - finalX));
    finalH = Math.max(0, Math.min(finalH, contentRect.height - finalY));

    // Convert to normalized coordinates (0..1)
    const x1 = contentRect.width > 0 ? finalX / contentRect.width : 0;
    const y1 = contentRect.height > 0 ? finalY / contentRect.height : 0;
    const x2 = contentRect.width > 0 ? (finalX + finalW) / contentRect.width : 0;
    const y2 = contentRect.height > 0 ? (finalY + finalH) / contentRect.height : 0;

    // Send normalized coords to parent in the same shape used elsewhere
    if (typeof setNewAnnotation === "function") {
      setNewAnnotation({
        markerDetails: {
          coordinates: {
            x1: clamp(x1, 0, 1),
            y1: clamp(y1, 0, 1),
            x2: clamp(x2, 0, 1),
            y2: clamp(y2, 0, 1),
          },
        },
      });
    }

    setDrawing(false);
  };

  // filter annotations by current time
  const filteredAnnotations = annotations?.filter((annotation) => {
    try {
      if (!playerRef || !playerRef.current || typeof playerRef.current.currentTime !== "function")
        return false;
      const currentTime = playerRef.current.currentTime();
      if (!annotation?.timeRange) return false;
      const { startTime, endTime } = annotation.timeRange;
      if (startTime === undefined || endTime === undefined) return false;
      return currentTime >= startTime && currentTime <= endTime;
    } catch (e) {
      return false;
    }
  });

  // helper: accept both normalized coords and pixel coords (if >1 assume pixel)
  const normalizeCoords = (coords) => {
    if (!coords) return { x1: 0, y1: 0, x2: 0, y2: 0 };
    const max = Math.max(Math.abs(coords.x1 ?? 0), Math.abs(coords.x2 ?? 0), Math.abs(coords.y1 ?? 0), Math.abs(coords.y2 ?? 0));
    // if looks normalized already
    if (max <= 1) {
      return {
        x1: clamp(coords.x1 ?? 0, 0, 1),
        y1: clamp(coords.y1 ?? 0, 0, 1),
        x2: clamp(coords.x2 ?? 0, 0, 1),
        y2: clamp(coords.y2 ?? 0, 0, 1),
      };
    }
    // else treat as pixels -> convert to normalized using current contentRect
    const w = contentRect.width || 1;
    const h = contentRect.height || 1;
    return {
      x1: clamp((coords.x1 ?? 0) / w, 0, 1),
      y1: clamp((coords.y1 ?? 0) / h, 0, 1),
      x2: clamp((coords.x2 ?? 0) / w, 0, 1),
      y2: clamp((coords.y2 ?? 0) / h, 0, 1),
    };
  };

  // nothing to render until measured
  if (!videoRef || !videoRef.current) return null;
  if (!contentRect || contentRect.width === 0 || contentRect.height === 0) return null;

  // render annotations: convert normalized -> pixels
  const renderAnnotations = () =>
    (filteredAnnotations || []).map((annotation, idx) => {
      const coordsRaw = annotation?.markerDetails?.coordinates || {};
      const norm = normalizeCoords(coordsRaw);

      // convert to pixel values inside contentRect
      const x1px = norm.x1 * contentRect.width;
      const y1px = norm.y1 * contentRect.height;
      const x2px = norm.x2 * contentRect.width;
      const y2px = norm.y2 * contentRect.height;

      const x = Math.max(0, Math.min(x1px, contentRect.width));
      const y = Math.max(0, Math.min(y1px, contentRect.height));
      const width = Math.max(0, Math.min(x2px - x1px, contentRect.width - x));
      const height = Math.max(0, Math.min(y2px - y1px, contentRect.height - y));

      return (
        <Rect
          key={annotation._id ?? idx}
          x={x}
          y={y}
          width={width}
          height={height}
          fill="rgba(255, 255, 0, 0)"
          stroke="yellow"
          strokeWidth={2}
          draggable={isPaused}
          onDragEnd={(e) => {
            const nx = e.target.x();
            const ny = e.target.y();
            const nw = e.target.width();
            const nh = e.target.height();
            // convert to normalized and send to parent
            if (typeof setNewAnnotation === "function") {
              const sx1 = clamp(nx / contentRect.width, 0, 1);
              const sy1 = clamp(ny / contentRect.height, 0, 1);
              const sx2 = clamp((nx + nw) / contentRect.width, 0, 1);
              const sy2 = clamp((ny + nh) / contentRect.height, 0, 1);
              setNewAnnotation({
                _id: annotation._id,
                markerDetails: {
                  coordinates: { x1: sx1, y1: sy1, x2: sx2, y2: sy2 },
                },
              });
            }
          }}
        />
      );
    });

  // preview while drawing (handles negative width/height)
  const renderNewAnnotation = () => {
    if (!newAnnotation) return null;

    // If newAnnotation has markerDetails.coordinates (normalized) that means parent provided final normalized rect;
    // else assume it's stage coords preview (startX/startY,x,y,width,height)
    if (newAnnotation.markerDetails && newAnnotation.markerDetails.coordinates) {
      const norm = normalizeCoords(newAnnotation.markerDetails.coordinates);
      const x1px = norm.x1 * contentRect.width;
      const y1px = norm.y1 * contentRect.height;
      const x2px = norm.x2 * contentRect.width;
      const y2px = norm.y2 * contentRect.height;
      const x = clamp(x1px, 0, contentRect.width);
      const y = clamp(y1px, 0, contentRect.height);
      const width = Math.max(0, Math.min(x2px - x1px, contentRect.width - x));
      const height = Math.max(0, Math.min(y2px - y1px, contentRect.height - y));
      return <Rect x={x} y={y} width={width} height={height} fill="rgba(255,0,0,0.04)" stroke="yellow" />;
    }

    // stage-preview mode
    const startX = newAnnotation.startX ?? newAnnotation.x ?? 0;
    const startY = newAnnotation.startY ?? newAnnotation.y ?? 0;
    const w = newAnnotation.width ?? 0;
    const h = newAnnotation.height ?? 0;
    const drawX = w >= 0 ? startX : startX + w;
    const drawY = h >= 0 ? startY : startY + h;
    const drawW = Math.abs(w);
    const drawH = Math.abs(h);
    const clampedX = clamp(drawX, 0, contentRect.width);
    const clampedY = clamp(drawY, 0, contentRect.height);
    const clampedW = Math.max(0, Math.min(drawW, contentRect.width - clampedX));
    const clampedH = Math.max(0, Math.min(drawH, contentRect.height - clampedY));
    return <Rect x={clampedX} y={clampedY} width={clampedW} height={clampedH} fill="rgba(255,0,0,0.04)" stroke="yellow" />;
  };

  // wrapper fixed or absolute depending on use cases
  const wrapperStyle = absoluteMode ? {
    position: "absolute",
    top: `${contentRect.offsetY}px`,
    left: `${contentRect.offsetX}px`,
    width: `${contentRect.width}px`,
    height: `${contentRect.height}px`,
    overflow: "hidden",
    pointerEvents: (drawing || isPaused) && !isPlayerSide ? "auto" : "none",
    zIndex: 2000,
    boxSizing: "border-box",
  } : {
    position: "fixed",
    top: `${contentRect.top}px`,
    left: `${contentRect.left}px`,
    width: `${contentRect.width}px`,
    height: `${contentRect.height}px`,
    overflow: "hidden",
    pointerEvents: (drawing || isPaused) && !isPlayerSide ? "auto" : "none",
    zIndex: 2000,
    boxSizing: "border-box",
    maxWidth: "100vw",
    maxHeight: "100vh",
  };

  return (
    <div style={wrapperStyle} className={styles.annotation_stage_wrapper}>
      <Stage
        ref={stageRef}
        width={contentRect.width}
        height={contentRect.height}
        onMouseDown={handleStartDrawing}
        onMouseMove={handleDrawing}
        onMouseUp={handleEndDrawing}
        onTouchStart={handleStartDrawing}
        onTouchMove={handleDrawing}
        onTouchEnd={handleEndDrawing}
        style={{
          width: "100%",
          height: "100%",
          touchAction: "none",
          cursor: drawing ? "crosshair" : "default",
          display: "block",
        }}
      >
        <Layer>
          {renderAnnotations()}
          {renderNewAnnotation()}
        </Layer>
      </Stage>
    </div>
  );
};

export default AnnotationStage;
